import { ResourceClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, MenuButton, Select, Switch, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Ban, ClipboardCheck, ListPlus, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type PartGridDTO, type PartGroupDTO } from "../../api/manufacturing";
import {
  StockCountClient,
  StockCountLineClient,
  type StockCountDTO,
  type StockCountLineDTO,
  type StockCountPostResult,
} from "../../api/stockCount";
import { NexpimView } from "../../views";
import { PartPicker } from "./PartPicker";
import { StockCountStatusBadge } from "./stockCountBadges";
import { StockMovementsGrid } from "./StockMovementsGrid";

/**
 * One count sheet.
 *
 * <p>While OPEN: parts are added from the ledger (by group, optionally every
 * active part for a wall-to-wall count) or one at a time, counts are typed
 * straight into the grid — blank means not counted yet — and the sheet is
 * posted once. Each line's expected quantity was frozen when it was added;
 * "Refresh expected" re-freezes the lines nobody has counted yet, and is meant
 * for just before counting starts. Posting corrects each counted line by
 * counted minus expected, so what moved during the count is kept.</p>
 *
 * <p>Once POSTED the sheet is a record, and the movements it made are shown
 * beneath it.</p>
 */
export function StockCountPage({ countId }: { countId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const counts = useMemo(() => new StockCountClient(http), [http]);
  const lines = useMemo(() => new StockCountLineClient(http), [http]);
  const [filling, setFilling] = useState(false);
  const [posting, setPosting] = useState(false);
  const [toCountOnly, setToCountOnly] = useState(false);

  const count = useQuery({
    queryKey: ["manufacturing", "stock-count", countId],
    queryFn: () => counts.findById(countId),
    enabled: allowed,
  });

  const table = useServerTable<StockCountLineDTO>({
    queryKey: ["manufacturing", "stock-count-line", countId, toCountOnly],
    fetchPage: (request) =>
      lines.find({
        ...request,
        filters: {
          ...(request.filters ?? {}),
          stock_count_id: [String(countId)],
          ...(toCountOnly ? { is_counted: ["false"] } : {}),
        },
      }),
    enabled: allowed,
  });

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-count"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-count-line", countId] });
  };

  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const refresh = useMutation({
    mutationFn: () => counts.refresh(countId),
    onSuccess: async () => {
      await refreshAll();
      toast.success(t("count.refreshed", "Expected quantities refreshed"));
    },
    onError: fail,
  });

  const cancel = useMutation({
    mutationFn: () => counts.cancel(countId),
    onSuccess: async () => {
      await refreshAll();
      toast.success(t("count.cancelled", "Count sheet cancelled"));
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => counts.remove(countId),
    onSuccess: async () => {
      // Leave first and drop the sheet from the cache: refreshing it here
      // would re-read a sheet that no longer exists.
      await navigate({ to: "/manufacturing/stock/counts" });
      queryClient.removeQueries({ queryKey: ["manufacturing", "stock-count", countId] });
      queryClient.removeQueries({ queryKey: ["manufacturing", "stock-count-line", countId] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-count"] });
      toast.success(t("count.deleted", "Count sheet deleted"));
    },
    onError: fail,
  });

  const addPart = useMutation({
    mutationFn: async (part: PartGridDTO) => {
      // The server answers a part already on the sheet with a bare 500; ask
      // first so the counter is told what happened.
      const existing = await lines.find({
        page: 0,
        size: 50,
        filters: { stock_count_id: [String(countId)], part_number: [part.number] },
      });
      if (existing.content.some((line) => line.part_id === part.id)) {
        throw new Error(t("count.add-part.duplicate", "{{part}} is already on this sheet.", { part: part.number }));
      }
      await lines.create({ id: 0, stock_count_id: countId, part_id: part.id, counted_quantity: null, note: null });
      return part;
    },
    onSuccess: async (part) => {
      await refreshAll();
      toast.success(t("count.add-part.done", "{{part}} added to the sheet", { part: part.number }));
    },
    onError: fail,
  });

  const data = count.data;
  const open = data?.status === "OPEN";
  const editable = canWrite && open;

  const cols = useMemo(() => {
    const c = defineColumns<StockCountLineDTO>();
    return [
      c.text("storage_location", (row) => row.storage_location ?? "", { headerKey: "column.count.bin", priority: 3 }),
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.part.name", priority: 3 }),
      c.number("expected_quantity", (row) => row.expected_quantity, { headerKey: "column.count.expected", filter: "range", priority: 2 }),
      c.number("counted_quantity", (row) => row.counted_quantity, { headerKey: "column.count.counted-quantity", filter: "range", priority: 1 }),
      c.custom(
        "variance",
        (row) => row.variance ?? 0,
        (row) =>
          row.variance == null ? (
            <span />
          ) : (
            <span className={"tabular-nums " + (row.variance > 0 ? "text-ok" : row.variance < 0 ? "text-risk" : "text-ink-faint")}>
              {row.variance > 0 ? "+" : ""}
              {formatNumber(row.variance)}
            </span>
          ),
        { headerKey: "column.count.variance", filter: "range", priority: 2 },
      ),
      c.number("current_quantity", (row) => row.current_quantity, {
        headerKey: "column.count.current",
        filter: "range",
        defaultVisible: false,
      }),
      // Neither is a queryable field on the server: sorting by them was refused with a 400.
      c.text("stock_unit_code", (row) => row.stock_unit_code ?? "", { headerKey: "column.bom.unit", filter: "none", enableSorting: false }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", filter: "none", enableSorting: false }),
    ];
  }, []);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("count.title.list", "Physical counts")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const parent = { to: "/manufacturing/stock/counts", label: t("count.title.list", "Physical counts") };
  if (!data) {
    return (
      <>
        <PageHeader parent={parent} title={t("count.title.list", "Physical counts")} />
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">
          {count.isLoading ? t("common.loading", "Loading…") : t("count.not-found", "That count sheet could not be loaded.")}
        </div>
      </>
    );
  }

  const lineCount = data.line_count ?? 0;
  const countedCount = data.counted_count ?? 0;

  return (
    <>
      <PageHeader
        parent={parent}
        title={t("count.title", "Count #{{n}}: {{name}}", { n: data.number, name: data.name })}
        subtitle={[data.warehouse_code, data.count_date].filter(Boolean).join(" · ")}
        badges={
          <>
            <StockCountStatusBadge status={data.status} />
            <Badge variant={countedCount === lineCount && lineCount > 0 ? "success" : "neutral"} size="sm">
              {t("count.progress", "{{counted}} of {{lines}} counted", { counted: countedCount, lines: lineCount })}
            </Badge>
            {(data.variance_count ?? 0) > 0 && (
              <Badge variant="warning" size="sm">
                {t("count.variances", "{{n}} differ", { n: data.variance_count })}
              </Badge>
            )}
          </>
        }
        actions={
          editable ? (
            <>
              {/* Three rarely used actions behind one menu: as buttons they
                  squeezed the sheet's title down to "Count #1:…". */}
              <MenuButton
                label={t("count.more", "More")}
                variant="ghost"
                icon={<MoreHorizontal className="size-4" />}
                disabled={remove.isPending || cancel.isPending || refresh.isPending}
                items={[
                  {
                    label: t("count.refresh", "Refresh expected"),
                    icon: <RefreshCw className="size-4" />,
                    onClick: () =>
                      void (async () => {
                        if (
                          await confirm({
                            title: t("count.refresh", "Refresh expected"),
                            message: t(
                              "count.refresh.message",
                              "Every line not yet counted takes the ledger balance as it is now as its expected quantity. Do this just before counting starts, not after: counted lines are left alone.",
                            ),
                            confirmLabel: t("count.refresh", "Refresh expected"),
                          })
                        ) {
                          refresh.mutate();
                        }
                      })(),
                  },
                  {
                    label: t("count.cancel", "Cancel sheet"),
                    icon: <Ban className="size-4" />,
                    onClick: () =>
                      void (async () => {
                        if (
                          await confirm({
                            title: t("count.cancel", "Cancel sheet"),
                            message: t("count.cancel.message", "The sheet stays on file as cancelled and nothing is posted."),
                            variant: "danger",
                            confirmLabel: t("count.cancel", "Cancel sheet"),
                          })
                        ) {
                          cancel.mutate();
                        }
                      })(),
                  },
                  {
                    label: t("count.delete", "Delete"),
                    icon: <Trash2 className="size-4" />,
                    onClick: () =>
                      void (async () => {
                        if (
                          await confirm({
                            title: t("count.delete.title", "Delete this sheet?"),
                            message: t("count.delete.message", "Nothing has been posted from it, so it can go entirely."),
                            variant: "danger",
                            confirmLabel: t("count.delete", "Delete"),
                          })
                        ) {
                          remove.mutate();
                        }
                      })(),
                  },
                ]}
              />
              <Button variant="secondary" onClick={() => setFilling(true)}>
                <ListPlus className="size-4" />
                {t("count.fill", "Add parts")}
              </Button>
              <Button variant="primary" disabled={lineCount === 0} onClick={() => setPosting(true)}>
                <ClipboardCheck className="size-4" />
                {t("count.post", "Post count")}
              </Button>
            </>
          ) : null
        }
      />

      {open ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-2 lg:px-6">
            {editable && (
              <div className="w-full max-w-md">
                <PartPicker
                  onPick={(part) => addPart.mutate(part)}
                  placeholder={t("count.add-part", "Add a part to this sheet")}
                />
              </div>
            )}
            <Switch checked={toCountOnly} onChange={setToCountOnly} label={t("count.to-count-only", "Still to count")} className="ml-auto" />
          </div>
          <DataTable<StockCountLineDTO>
            data={table.rows}
            columns={cols}
            getRowId={(row) => String(row.id)}
            stateKey="/manufacturing/stock/counts/lines"
            loading={table.loading}
            server={table.mode}
            editor={
              editable
                ? {
                    fields: {
                      counted_quantity: {
                        kind: "number",
                        get: (row) => row.counted_quantity,
                        set: (row, value) => ({ ...row, counted_quantity: value.trim() === "" ? null : Number(value) }),
                        placeholder: t("count.not-counted", "not counted"),
                      },
                      note: {
                        kind: "text",
                        get: (row) => row.note,
                        set: (row, value) => ({ ...row, note: value === "" ? null : value }),
                      },
                    },
                    validate: (row) =>
                      row.counted_quantity != null && (Number.isNaN(row.counted_quantity) || row.counted_quantity < 0)
                        ? { counted_quantity: t("count.counted.invalid", "A count is zero or more.") }
                        : null,
                    onSave: async (row) => {
                      await lines.update(row);
                      await refreshAll();
                    },
                    onDelete: async (row) => {
                      await lines.remove(row.id);
                      await refreshAll();
                    },
                    deleteConfirm: () => ({ title: t("count.line.delete", "Remove this part from the sheet?") }),
                  }
                : undefined
            }
            emptyMessage={t("count.lines.empty", "No parts on this sheet yet. Add them from the ledger or one at a time.")}
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <DataTable<StockCountLineDTO>
            data={table.rows}
            columns={cols}
            getRowId={(row) => String(row.id)}
            stateKey="/manufacturing/stock/counts/lines"
            loading={table.loading}
            server={table.mode}
            emptyMessage={t("table.empty", "Nothing to show")}
          />
          {data.stock_document_id != null && (
            <section className="flex min-h-[18rem] flex-col border-t border-line">
              <h3 className="px-4 py-2 text-[13px] font-semibold lg:px-6">
                {t("count.movements", "Movements posted by this count (document #{{id}})", { id: data.stock_document_id })}
              </h3>
              <StockMovementsGrid stockDocumentId={data.stock_document_id} />
            </section>
          )}
        </div>
      )}

      {filling && <FillDialog count={data} onClose={() => setFilling(false)} onFilled={refreshAll} />}
      {posting && <PostDialog count={data} onClose={() => setPosting(false)} onPosted={refreshAll} />}
    </>
  );
}

/** Adds the parts the ledger holds in the warehouse, optionally one group, optionally every part the warehouse keeps. */
function FillDialog({ count, onClose, onFilled }: { count: StockCountDTO; onClose: () => void; onFilled: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [groupId, setGroupId] = useState("");
  const [includeZero, setIncludeZero] = useState(false);

  const groups = useQuery({
    queryKey: ["part-group", "all"],
    queryFn: () => new ResourceClient<PartGroupDTO>(http, "/manufacturing/part-group").findAll(),
    staleTime: 30 * 60_000,
  });

  const fill = useMutation({
    mutationFn: () =>
      new StockCountClient(http).fill(count.id, { part_group_id: groupId === "" ? null : Number(groupId), include_zero: includeZero }),
    onSuccess: async (updated) => {
      await onFilled();
      toast.success(t("count.filled", "{{n}} parts on the sheet", { n: updated.line_count ?? 0 }));
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("count.fill", "Add parts")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={fill.isPending} onClick={() => fill.mutate()}>
            {t("count.fill.go", "Add to the sheet")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("count.fill.group", "Part group")} htmlFor="count-fill-group">
          <Select
            id="count-fill-group"
            value={groupId}
            options={[
              { value: "", label: t("count.fill.all-groups", "Every group") },
              ...(groups.data ?? [])
                .filter((group) => group.is_active)
                .sort((a, b) => a.code.localeCompare(b.code))
                .map((group) => ({ value: String(group.id), label: `${group.code} · ${group.name}` })),
            ]}
            onChange={(event) => setGroupId(event.target.value)}
          />
        </Field>
        <Switch checked={includeZero} onChange={setIncludeZero} label={t("count.fill.include-zero", "Also the parts {{warehouse}} keeps at zero", { warehouse: count.warehouse_code ?? "" })} />
        <p className="text-[12px] text-ink-muted">
          {includeZero
            ? t("count.fill.hint-zero", "Every part {{warehouse}} keeps (its stocking points) is added, at zero or not: for a wall-to-wall count, where finding something unexpected is the point.", { warehouse: count.warehouse_code ?? "" })
            : t("count.fill.hint", "Only parts with a balance in {{warehouse}} are added.", { warehouse: count.warehouse_code ?? "" })}{" "}
          {t("count.fill.hint-kept", "Parts already on the sheet keep their line and their count.")}
        </p>
      </div>
    </Dialog>
  );
}

/** Posting: one COUNT document, one movement per counted line that differs. */
function PostDialog({ count, onClose, onPosted }: { count: StockCountDTO; onClose: () => void; onPosted: () => Promise<void> }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const [zeroUncounted, setZeroUncounted] = useState(false);
  const [note, setNote] = useState("");
  const [result, setResult] = useState<StockCountPostResult | null>(null);
  const uncounted = (count.line_count ?? 0) - (count.counted_count ?? 0);
  // Nothing counted and nothing to zero: posting would freeze the sheet for nothing.
  const nothingToPost = (count.counted_count ?? 0) === 0 && !zeroUncounted;

  const post = useMutation({
    mutationFn: () => new StockCountClient(http).post(count.id, { zero_uncounted: zeroUncounted, note: note.trim() || null }),
    onSuccess: async (posted) => {
      setResult(posted);
      await onPosted();
      // Posting moved the ledger: every balance on screen elsewhere is stale.
      for (const key of ["stock-balance", "stock-movement", "part-grid", "part", "part-picker"]) {
        await queryClient.invalidateQueries({ queryKey: ["manufacturing", key] });
      }
      toast.success(t("count.posted", "Count posted: document #{{id}}", { id: posted.stock_document_id }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("count.post.title", "Post count #{{n}}", { n: count.number })}
      actions={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant={zeroUncounted ? "danger" : "primary"}
              disabled={nothingToPost}
              loading={post.isPending}
              onClick={() => post.mutate()}
            >
              {t("count.post", "Post count")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-2 text-[13px]">
          <p>{t("count.post.done", "Document #{{id}} posted.", { id: result.stock_document_id })}</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="warning" size="sm">
              {t("count.post.movements", "{{n}} corrected", { n: result.movements_posted })}
            </Badge>
            <Badge variant="success" size="sm">
              {t("count.post.matching", "{{n}} matched", { n: result.lines_matching })}
            </Badge>
            <Badge variant="neutral" size="sm">
              {t("count.post.skipped", "{{n}} not counted, left alone", { n: result.lines_skipped })}
            </Badge>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-[13px]">
          <p className="text-ink-muted">
            {t(
              "count.post.explain",
              "Each counted line corrects the ledger by what was found minus what was expected when the line was added. Movements posted since then are kept. The sheet is frozen afterwards.",
            )}
          </p>
          {nothingToPost && (
            <p className="text-[12px] text-warn">
              {t("count.post.nothing", "No line has been counted yet. Type the counts in the sheet first.")}
            </p>
          )}
          {uncounted > 0 && (
            <>
              <Switch
                checked={zeroUncounted}
                onChange={setZeroUncounted}
                label={t("count.post.zero-uncounted", "Treat the {{n}} uncounted lines as found empty", { n: uncounted })}
              />
              <p className={"text-[12px] " + (zeroUncounted ? "text-risk" : "text-ink-muted")}>
                {zeroUncounted
                  ? t(
                      "count.post.zero-warning",
                      "Every line nobody counted will be set to zero in the ledger. Only do this for a wall-to-wall count where every shelf was counted.",
                    )
                  : t("count.post.skip-hint", "Uncounted lines are skipped: their balance stays as it is.")}
              </p>
            </>
          )}
          <Field label={t("form.field.note", "Note")} htmlFor="count-post-note">
            <Textarea id="count-post-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}
